import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services';
import type { Site, SitePayload, SiteSummary } from './types';

export const useSite = (id: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ['sites', id] as const,
    queryFn: (): Promise<Site> => api.get(`sites/${id}`).then((res) => res.data as Site),
    refetchOnMount: 'always',
    enabled: Boolean(id) && enabled,
  });

/**
 * Every caller shares the raw list under `['sites']`; narrow it with `selectFn`, never in a
 * `queryFn`, or the narrowed list lands in the shared cache entry and leaks into the others.
 */
export const useSites = <T = SiteSummary[]>(selectFn?: (data: SiteSummary[]) => T) =>
  useQuery({
    queryKey: ['sites'],
    queryFn: (): Promise<SiteSummary[]> => api.get('sites').then((res) => res.data as SiteSummary[]),
    ...(selectFn && { select: selectFn }),
  });

// Array keys throughout: react-query 4 reads a bare string passed to `invalidateQueries` as a
// filters object, which matched every query in the cache rather than just the sites.

export const useCreateSite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (site: SitePayload): Promise<Site> => api.post('sites', site).then((res) => res.data as Site),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
};

export const useUpdateSite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (site: Site): Promise<Site> => api.put(`sites/${site.id}`, site).then((res) => res.data as Site),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
};

export const useDeleteSite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`sites/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
};
